<template>
  <component :is="tag" class="base-dropdown">
    <!-- Trigger -->
    <slot name="trigger" v-bind="{ toggleState }"></slot>

    <!-- Target -->
    <MountingPortal
      v-if="isModal"
      :name="`base-dropdown--${id}`"
      append
      :mount-to="portalModalDestination"
    >
      <BaseModal
        v-model="localIsExpanded"
        v-bind="modalOptions"
        :class="modalClass"
      >
        <template v-slot:[modalTargetSlot]>
          <slot name="target" v-bind="{ toggleState }"></slot>
        </template>

        <template #backdrop>
          <slot name="modal-backdrop" v-bind="{ toggleState }"></slot>
        </template>

        <template #header>
          <slot name="modal-header" v-bind="{ toggleState }"></slot>
        </template>

        <template #header-content>
          <slot name="modal-header-content" v-bind="{ toggleState }"></slot>
        </template>

        <template #header-title>
          <slot name="modal-header-title" v-bind="{ toggleState }"></slot>
        </template>

        <template #header-close-btn>
          <slot name="modal-header-close-btn" v-bind="{ toggleState }"></slot>
        </template>

        <template #header-close-btn-label>
          <slot
            name="modal-header-close-btn-label"
            v-bind="{ toggleState }"
          ></slot>
        </template>

        <template #subheader>
          <slot name="modal-subheader" v-bind="{ toggleState }"></slot>
        </template>

        <template #subheader-content>
          <slot name="modal-subheader-content" v-bind="{ toggleState }"></slot>
        </template>

        <template #footer>
          <slot name="modal-footer" v-bind="{ toggleState }"></slot>
        </template>

        <template #footer-content>
          <slot name="modal-footer-content" v-bind="{ toggleState }"></slot>
        </template>

        <template #actions>
          <slot name="modal-actions" v-bind="{ toggleState }"></slot>
        </template>

        <template #actions-container>
          <slot name="modal-actions-container" v-bind="{ toggleState }"></slot>
        </template>

        <template #action-toolbar>
          <slot name="modal-action-toolbar" v-bind="{ toggleState }"></slot>
        </template>
      </BaseModal>
    </MountingPortal>

    <MountingPortal
      v-else-if="isPortalDestinationOutsideApp"
      append
      :mount-to="portalDestination"
    >
      <div
        v-if="isMounted && localIsExpanded"
        ref="target"
        :style="getDropdownPosition()"
        class="base-dropdown__target"
        :class="dropdownTargetClass"
      >
        <slot name="target" v-bind="{ toggleState }"></slot>
      </div>
    </MountingPortal>

    <Portal v-else append :to="portalDestination">
      <div
        v-if="isMounted && localIsExpanded"
        ref="target"
        :style="getDropdownPosition()"
        class="base-dropdown__target"
        :class="dropdownTargetClass"
      >
        <slot name="target" v-bind="{ toggleState }"></slot>
      </div>
    </Portal>
  </component>
</template>

<script>
import { randomStr } from '../utils/String';
import BaseModal from './BaseModal';
import BaseDropdownModalProps from '../mixins/BaseDropdownModalProps';

const EVENT_LIST = ['click', 'touchstart'];

/**
 * @enum
 * @readonly
 */
export const Placement = Object.freeze({
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_RIGHT: 'bottom-right',
  RIGHT: 'right',
});

export default {
  name: 'BaseDropdown',
  components: { BaseModal },
  mixins: [BaseDropdownModalProps],
  model: {
    prop: 'expanded',
    event: 'toggle',
  },
  props: {
    expanded: { type: Boolean },
    fitParent: { type: Boolean },
    fitViewport: { type: Boolean },
    noAutoHide: { type: Boolean },
    tag: {
      type: String,
      default: 'div',
    },
    placement: {
      type: String,
      default: Placement.BOTTOM_LEFT,
      validator(v) {
        return Object.values(Placement).includes(v);
      },
    },
    dropdownTargetClass: {
      type: [String, Array, Object],
      default: undefined,
    },
    portalDestination: {
      type: String,
      default: 'portal-container--app-footer',
    },
    portalModalDestination: {
      type: String,
      default: '#portal-container--footer',
    },
  },
  data() {
    return {
      id: randomStr(),
      localIsExpanded: null,
      isClickEventBound: false,
      isMounted: false,
    };
  },
  computed: {
    isModal() {
      return (
        this.modal ||
        (this.modalBreakpoint && !this.$matchMedia[this.modalBreakpoint])
      );
    },
    isPortalDestinationOutsideApp() {
      return this.portalDestination.charAt(0) === '#';
    },
  },
  watch: {
    expanded: {
      handler(v) {
        this.localIsExpanded = v;
      },
      immediate: true,
    },
    localIsExpanded(v) {
      this.$emit('toggle', v);
      this.$emit(v ? 'expand' : 'collapse');

      if (!this.noAutoHide && !this.isModal) {
        if (v) {
          this.bindClickEvent();
        } else {
          this.unbindClickEvent();
        }
      }
    },
    noAutoHide: {
      handler(v) {
        if (v) {
          this.unbindClickEvent();
        } else if (this.localIsExpanded) {
          this.bindClickEvent();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.isMounted = true;
  },
  beforeDestroy() {
    this.unbindClickEvent();
  },
  methods: {
    toggleState() {
      this.localIsExpanded = !this.localIsExpanded;
    },
    getDropdownPosition() {
      const o = {};
      const { top, left, width, height } = this.$el.getBoundingClientRect();
      const isPlacementBottom = [
        Placement.BOTTOM_LEFT,
        Placement.BOTTOM_RIGHT,
      ].includes(this.placement);

      if (this.fitParent) {
        if (isPlacementBottom) {
          o.width = `${width}px`;
        } else if (this.placement === Placement.RIGHT) {
          o.height = `${height}px`;
        }
      }

      switch (true) {
        case isPlacementBottom: {
          o.top = `${top + height + window.document.body.scrollTop}px`;

          if (this.placement === Placement.BOTTOM_LEFT) {
            o.left = `${left}px`;
          } else if (this.placement === Placement.BOTTOM_RIGHT) {
            o.right = `${window.innerWidth - (left + width)}px`;
          }

          if (this.fitViewport) {
            o.maxHeight = `${window.innerHeight - (top + height)}px`;
            o.overflowY = 'auto';
          }

          break;
        }

        case this.placement === Placement.RIGHT: {
          o.top = o.top = `${top + window.document.body.scrollTop}px`;
          o.left = `${left + width + window.document.body.scrollLeft}px`;

          if (this.fitViewport) {
            o.maxWidth = `${window.innerWidth - (left + width)}px`;
            o.overflowX = 'auto';
          }

          break;
        }

        default: {
          throw new RangeError('Invalid placement');
        }
      }

      return o;
    },
    clickCb(e) {
      if (
        !(this.$refs.target && this.$refs.target.contains(e.target)) &&
        !this.$el.contains(e.target)
      ) {
        this.toggleState();
      }
    },
    bindClickEvent() {
      if (!this.isClickEventBound) {
        // Using setTimeout to delay event listener binding to the next event loop
        // This avoids immediate call of click event
        setTimeout(() => {
          EVENT_LIST.forEach(event =>
            window.addEventListener(event, this.clickCb)
          );

          this.isClickEventBound = true;
        }, 0);
      }
    },
    unbindClickEvent() {
      if (this.isClickEventBound) {
        EVENT_LIST.forEach(event =>
          window.removeEventListener(event, this.clickCb)
        );

        this.isClickEventBound = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.base-dropdown {
  &__target {
    position: absolute;
    z-index: 1000;
  }
}
</style>
